import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faTableColumns, faUserPlus, faCalendarCheck, faHourglassHalf } from '@fortawesome/pro-regular-svg-icons';

import PortalHeader from '../../components/Portal/PortalHeader';
import WaButton from '../../components/wa/WaButton';
import { fetchInquiryLeads, setInquiryStatus, convertInquiryToStudents, interestList } from '../../utils/inquiryService';
import { isUnfinishedStep1, step1Line } from '../../utils/registerStep1';
import {
	DEFAULT_INQUIRY_SORT,
	SORT_COLUMNS,
	nextSort,
	sortInquiries,
	tsToMs,
} from './inquirySort';
import { csvEscapeCell } from '../../utils/csv';
import '../../components/Portal/portalSurface.css';
import './Inquiries.css';

// ── helpers ──
function fmtDate(ts) {
	const ms = tsToMs(ts);
	return ms
		? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
		: '—';
}
// A free-trial booking's date/time (August 2026 phase). KEPT after the /trial
// page and trialSlots were deleted on 2026-09-03: the lead still carries its
// appointment, and this is now the only record of when a family was seen.
// The stored values are studio-local STRINGS ('2026-08-11', '10:30') and are
// deliberately not put through a Date for the date part: `new Date('2026-08-11')`
// parses as UTC midnight and renders as Aug 10 west of Greenwich, which would
// print the wrong day to the person about to teach the lesson.
function fmtTrialSlot(tb) {
	if (!tb || !tb.date) return '—';
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(tb.date);
	const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	const date = m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : tb.date;
	const t = /^(\d{1,2}):(\d{2})$/.exec(tb.startTime || '');
	if (!t) return date;
	const h = Number(t[1]);
	return `${date}, ${h % 12 === 0 ? 12 : h % 12}:${t[2]} ${h >= 12 ? 'PM' : 'AM'}`;
}
// Names are stored combined ("First Last"). First token → first name, the rest
// → last name. Known limitation: mis-splits multi-word / hyphenated names.
function splitName(full) {
	const parts = String(full || '').trim().split(/\s+/);
	if (parts.length === 0 || parts[0] === '') return { first: '', last: '' };
	return { first: parts[0], last: parts.slice(1).join(' ') };
}

// Status → .ps-badge variant. The portal palette carries five variants for the
// six statuses, so `contacted` takes the base (neutral navy) pill; the label
// text disambiguates. Comfort / Comfort Dark corrections come for free from
// comfort-portal.css, which the old Bootstrap <Badge bg=…> never inherited.
const PS_BADGE = {
	new: 'ps-badge--info',
	contacted: '',
	trial: 'ps-badge--warning',
	offered: 'ps-badge--neutral',
	converted: 'ps-badge--success',
	reviewed: 'ps-badge--success', // legacy
	archived: 'ps-badge--neutral',
};

// Manual funnel stages an employee can set (in order). `converted` is excluded —
// it's set only by the Convert action. `reviewed` is legacy (older leads only).
const FUNNEL = ['new', 'contacted', 'trial', 'offered', 'archived'];
const STATUS_LABEL = {
	new: 'New',
	contacted: 'Contacted',
	trial: 'Trial',
	offered: 'Offered',
	converted: 'Converted',
	reviewed: 'Reviewed',
	archived: 'Archived',
};

// "First Last" → { first, last } for prefilling the convert dialog.
function splitFullName(full) {
	const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
	if (!parts.length) return { first: '', last: '' };
	return { first: parts[0], last: parts.slice(1).join(' ') };
}

// Referral, condensed: "Friend referral · The Smiths".
function referralText(lead) {
	const src = lead.referralSource || '';
	const who = lead.referredBy || '';
	if (src && who) return `${src} · ${who}`;
	return src || who || '—';
}

// Columns the desktop picker can hide, in table order. Student(s) and Actions
// are locked — never offered, never hidden. Interest / Location / Referral
// default off: all three already appear in the tap-to-expand detail row, so the
// default 6-column view fits most windows without scrolling right.
const TOGGLEABLE = [
	{ key: 'parent', label: 'Parent' },
	{ key: 'contact', label: 'Contact' },
	{ key: 'interest', label: 'Interest' },
	{ key: 'location', label: 'Location' },
	{ key: 'referral', label: 'Referral' },
	{ key: 'status', label: 'Status' },
	{ key: 'submitted', label: 'Submitted' },
];

// A sortable column header.
//
// The clickable element is a real <button> rather than an onClick on the <th>
// so the column is reachable by keyboard; `aria-sort` on the th is what tells a
// screen reader which column is driving the order and which way. Same contract
// as the Slot Editor's SortableTh — deliberately mirrored so the two tables
// behave identically under a screen reader.
//
// Defined at MODULE scope, not inside Inquiries. A component declared in the
// render body gets a new function identity every render, so React unmounts and
// remounts it — throwing focus off the header button on the very click that
// sorted it, which breaks the keyboard path this markup exists to provide.
const SortableTh = ({ colKey, label, className = '', sort, onSort }) => {
	const active = sort.key === colKey;
	return (
		<th
			className={`${className} ps-sortable`.trim()}
			aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
			{/* title, because at the narrowest real layout — a 1280 window with the
			    sidebar expanded — nine headers do not fit and some still truncate.
			    A clipped VALUE has the expand row behind it; a clipped header had
			    nothing until this. */}
			<button type='button' className='ps-sort-btn' title={label} onClick={() => onSort(colKey)}>
				{/* The label is its own element so it can ellipsize independently of
				    the sort icon — see .ps-sort-btn__label in portalSurface.css. */}
				<span className='ps-sort-btn__label'>{label}</span>
				<i
					className={`fa-sharp fa-solid ${
						active
							? sort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'
							: 'fa-arrows-up-down ps-sort-btn__idle'
					}`}
					aria-hidden='true'
				/>
			</button>
		</th>
	);
};

function toCsv(leads) {
	const headers = [
		'Student First Name', 'Student Last Name', 'Age', 'Pronouns', 'School/Grade',
		'Lesson Interest', 'Location Preference', 'Parent First Name', 'Parent Last Name',
		'Email', 'Phone', 'Address', 'Heard About Us', 'Who To Thank', 'Notes', 'Status', 'Submitted',
	];
	const lines = [headers.join(',')];
	for (const l of leads) {
		const parent = splitName(l.parent?.name);
		const students = Array.isArray(l.students) && l.students.length ? l.students : [{}];
		// One row per student; repeat the parent columns so MMS imports each
		// student with their guardian.
		for (const s of students) {
			const student = splitName(s.name);
			lines.push(
				[
					student.first, student.last, s.age, s.pronouns, s.school,
					interestList(s.lessonInterest).join(', '), s.locationPreference,
					parent.first, parent.last, l.parent?.email, l.parent?.phone, l.parent?.address,
					l.referralSource, l.referredBy, l.notes, l.status, fmtDate(l.createdAt),
				].map(csvEscapeCell).join(',')
			);
		}
	}
	return lines.join('\n');
}

const Inquiries = () => {
	const [leads, setLeads] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	const [statusFilter, setStatusFilter] = useState('all');
	const [search, setSearch] = useState('');
	const [expandedId, setExpandedId] = useState(null);
	const [busyId, setBusyId] = useState(null);

	// Per-session on purpose (the Slot Editor precedent): sorting is a way of
	// looking at the queue right now — "who has been waiting longest", "which
	// are still New" — not a stored preference. Coming back tomorrow to a table
	// silently ordered by Location would be a surprise, so every load starts
	// newest-first, the order this page has always had.
	const [sort, setSort] = useState(DEFAULT_INQUIRY_SORT);
	const handleSort = (key) => setSort((prev) => nextSort(prev, key));

	// Desktop column visibility. In-memory only (v1) — matches the sort-control
	// precedent; localStorage persistence is a noted fast-follow.
	const [visibleCols, setVisibleCols] = useState(
		() => new Set(['parent', 'contact', 'status', 'submitted'])
	);
	const toggleCol = (key) =>
		setVisibleCols((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	// Hiding is done with CSS gated to ≥641px, NOT by dropping cells from the
	// DOM — an unrendered <td> would also vanish from the ≤640px stacked cards,
	// which must always show every field regardless of picker state.
	const tableHiddenClasses = TOGGLEABLE
		.filter((c) => !visibleCols.has(c.key))
		.map((c) => `is-hidden--${c.key}`)
		.join(' ');

	// Convert-to-student dialog: holds the lead being converted + an editable copy
	// of the guardian/students the employee reviews before confirming.
	const [convert, setConvert] = useState(null);
	const convertRef = useRef(null);
	useEffect(() => {
		if (convertRef.current) convertRef.current.open = !!convert;
	}, [convert]);
	// React 18 doesn't wire the custom element's own events, so `onWaAfterHide`
	// never fired and closing via ESC / backdrop left `convert` stale (#7).
	// Attach the WA close event explicitly (App.js drawer pattern). The dialog
	// is always mounted, so this runs once.
	useEffect(() => {
		const el = convertRef.current;
		if (!el) return undefined;
		const onHide = () => setConvert(null);
		el.addEventListener('wa-after-hide', onHide);
		return () => el.removeEventListener('wa-after-hide', onHide);
	}, []);

	useEffect(() => {
		let active = true;
		(async () => {
			try {
				const data = await fetchInquiryLeads();
				if (active) setLeads(data);
			} catch (err) {
				console.error('Failed to load inquiries:', err);
				if (active) setError('Could not load student inquiries.');
			} finally {
				if (active) setLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, []);

	const filteredLeads = useMemo(() => {
		const term = search.trim().toLowerCase();
		return leads.filter((l) => {
			if (statusFilter !== 'all' && (l.status || 'new') !== statusFilter) return false;
			if (term) {
				const studentNames = (l.students || []).map((s) => s.name).join(' ');
				const hay = `${studentNames} ${l.parent?.name || ''} ${l.parent?.email || ''}`.toLowerCase();
				if (!hay.includes(term)) return false;
			}
			return true;
		});
	}, [leads, statusFilter, search]);

	const visibleLeads = useMemo(() => sortInquiries(filteredLeads, sort), [filteredLeads, sort]);

	const handleExport = () => {
		// Export what is on screen, in the order it is on screen — a CSV that
		// silently reverts to newest-first would not match the table the person
		// just sorted before hitting the button.
		const csv = toCsv(visibleLeads);
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `student-inquiries-${statusFilter}.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	// Optimistic status change; reverts on failure.
	const changeStatus = async (lead, status) => {
		const prev = lead.status;
		setBusyId(lead.id);
		setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
		try {
			await setInquiryStatus(lead.id, status);
		} catch (err) {
			console.error('Failed to update inquiry status:', err);
			setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: prev } : l)));
			setError('Could not update that inquiry. Please try again.');
		} finally {
			setBusyId(null);
		}
	};

	// Open the convert dialog, prefilled from the lead (names split into
	// first/last; instrument seeded from the child's lesson interest).
	const openConvert = (lead) => {
		const g = splitFullName(lead.parent?.name);
		setConvert({
			leadId: lead.id,
			guardian: {
				firstName: g.first,
				lastName: g.last,
				email: lead.parent?.email || '',
				phone: lead.parent?.phone || '',
				address: lead.parent?.address || '',
			},
			students: (lead.students || []).map((s) => {
				const sn = splitFullName(s.name);
				return {
					firstName: sn.first,
					lastName: sn.last,
					age: s.age || '',
					// Primary instrument = first interest; extra interests stay on the
					// lead as context (multi-instrument = manual admin action).
					instrument: interestList(s.lessonInterest)[0] || 'Voice',
				};
			}),
			saving: false,
			error: '',
			result: null,
		});
	};
	const setGuardian = (field, value) =>
		setConvert((c) => ({ ...c, guardian: { ...c.guardian, [field]: value } }));
	const updateStudent = (i, field, value) =>
		setConvert((c) => ({
			...c,
			students: c.students.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)),
		}));

	const submitConvert = async () => {
		if (!convert) return;
		setConvert((c) => ({ ...c, saving: true, error: '' }));
		try {
			const res = await convertInquiryToStudents({
				leadId: convert.leadId,
				guardian: convert.guardian,
				students: convert.students,
			});
			// The server also archives the same guardian's other live funnel leads
			// (e.g. the trial booking beside a converted inquiry) so their Convert
			// buttons can't mint a duplicate student — mirror that here or the
			// stale rows keep the trap on screen until a reload.
			const archived = new Set(res.archivedLeadIds || []);
			setLeads((ls) =>
				ls.map((l) => {
					if (l.id === convert.leadId)
						return { ...l, status: 'converted', familyId: res.familyId, studentIds: res.studentIds };
					if (archived.has(l.id)) return { ...l, status: 'archived' };
					return l;
				})
			);
			setConvert((c) => ({ ...c, saving: false, result: res }));
		} catch (err) {
			console.error('Convert failed:', err);
			setConvert((c) => ({ ...c, saving: false, error: err.message || 'Conversion failed.' }));
		}
	};

	const counts = useMemo(() => {
		const c = {};
		for (const l of leads) {
			const s = l.status || 'new';
			c[s] = (c[s] || 0) + 1;
		}
		return c;
	}, [leads]);

	return (
		<>
			<PortalHeader
				title='Student Inquiries'
				subtitle='New-student registrations — review and export to MMS.'
			/>
			<div className='ps-page inq-page'>
				{loading && (
					<div className='ps-spinner' role='status' aria-label='Loading' />
				)}

				{!loading && error && (
					<div className='ps-alert ps-alert--danger'>{error}</div>
				)}

				{/* Toolbar, count and table share one shrink-to-fit wrapper so the
				    filters/Export row tracks the table's width instead of running the
				    full viewport while the table hugs its columns. */}
				{!loading && !error && (
					<div className='inq-fluid'>
						{/* Filters */}
						<div className='ps-toolbar'>
							<div className='ps-field'>
								<label className='ps-label' htmlFor='inq-status'>
									Status
								</label>
								<select
									id='inq-status'
									className='ps-select'
									value={statusFilter}
									onChange={(e) => setStatusFilter(e.target.value)}>
									<option value='all'>All statuses</option>
									<option value='new'>New</option>
									<option value='contacted'>Contacted</option>
									<option value='trial'>Trial</option>
									<option value='offered'>Offered</option>
									<option value='converted'>Converted</option>
									<option value='archived'>Archived</option>
								</select>
							</div>
							<div className='ps-field flex-grow-1'>
								<label className='ps-label' htmlFor='inq-search'>
									Search
								</label>
								<input
									id='inq-search'
									type='search'
									className='ps-input'
									placeholder='Student, parent, or email'
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</div>
							{/* Sort, for phones ONLY — the mirror image of the column picker
							    below. At ≤640px the table is stacked cards and portalSurface
							    clips <thead> to 1px, so the sortable headers are unreachable
							    there; unlike hidden columns (every field still shows on a
							    card) that is a real loss, because order is the whole point of
							    a queue. Hidden ≥641px, where the headers do the job. */}
							<div className='ps-field inq-sortmobile'>
								<label className='ps-label' htmlFor='inq-sort'>
									Sort
								</label>
								<div className='inq-sortmobile__row'>
									<select
										id='inq-sort'
										className='ps-select'
										value={sort.key}
										onChange={(e) => setSort({ key: e.target.value, direction: sort.direction })}>
										{SORT_COLUMNS.map((c) => (
											<option key={c.key} value={c.key}>
												{c.label}
											</option>
										))}
									</select>
									<button
										type='button'
										className='ps-btn'
										aria-label={
											sort.direction === 'asc' ? 'Sorted ascending — switch to descending' : 'Sorted descending — switch to ascending'
										}
										onClick={() =>
											setSort((prev) => ({
												...prev,
												direction: prev.direction === 'asc' ? 'desc' : 'asc',
											}))
										}>
										<i
											className={`fa-sharp fa-solid ${sort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'}`}
											aria-hidden='true'
										/>
									</button>
								</div>
							</div>
							{/* Desktop-only affordance — hidden ≤640px, where the table is
							    already stacked cards showing every field. */}
							<details className='ps-menu inq-colmenu'>
								<summary className='ps-btn'>
									<FontAwesomeIcon icon={faTableColumns} /> Columns
								</summary>
								<div className='ps-menu__panel'>
									<div className='ps-menu__header'>Show columns</div>
									{TOGGLEABLE.map((c) => (
										<label className='ps-menu__item' key={c.key}>
											<input
												type='checkbox'
												checked={visibleCols.has(c.key)}
												onChange={() => toggleCol(c.key)}
											/>
											{c.label}
										</label>
									))}
								</div>
							</details>
							<wa-button
								appearance='outlined'
								variant='brand'
								size='medium'
								onClick={handleExport}>
								Export CSV
							</wa-button>
						</div>

						<p className='inq-count'>
							{visibleLeads.length} inquir{visibleLeads.length === 1 ? 'y' : 'ies'}
							{['new', 'contacted', 'trial', 'offered', 'converted', 'archived']
								.filter((s) => counts[s])
								.map((s) => ` · ${counts[s]} ${STATUS_LABEL[s].toLowerCase()}`)
								.join('')}
						</p>

						{/* Queue */}
						<div className='ps-table-wrap'>
							<table className={`ps-table ps-table--stack ps-table--fit ${tableHiddenClasses}`}>
								<thead>
									<tr>
										{/* Actions is the only unsortable column — it holds controls,
										    not data. Every other header sorts. */}
										<SortableTh colKey='student' label='Student(s)' className='col--student' sort={sort} onSort={handleSort} />
										<SortableTh colKey='parent' label='Parent' className='col--parent' sort={sort} onSort={handleSort} />
										<SortableTh colKey='contact' label='Contact' className='col--contact' sort={sort} onSort={handleSort} />
										<SortableTh colKey='interest' label='Interest' className='col--interest' sort={sort} onSort={handleSort} />
										<SortableTh colKey='location' label='Location' className='col--location' sort={sort} onSort={handleSort} />
										<SortableTh colKey='referral' label='Referral' className='col--referral' sort={sort} onSort={handleSort} />
										<SortableTh colKey='status' label='Status' className='col--status' sort={sort} onSort={handleSort} />
										<SortableTh colKey='submitted' label='Submitted' className='col--submitted' sort={sort} onSort={handleSort} />
										<th className='col--actions'>Actions</th>
									</tr>
								</thead>
								<tbody>
									{visibleLeads.length === 0 && (
										<tr>
											<td colSpan={9} className='ps-cell-full'>
												<div className='ps-empty'>
													No inquiries match these filters.
												</div>
											</td>
										</tr>
									)}
									{visibleLeads.map((l) => {
										const status = l.status || 'new';
										const students = l.students || [];
										const first = students[0];
										const extra = students.length - 1;
										return (
											<React.Fragment key={l.id}>
												<tr
													className='inq-row'
													onClick={() => setExpandedId(expandedId === l.id ? null : l.id)}>
													<td className='col--student' data-label='Student(s)'>
														{l.kind === 'contact' ? (
															<span className='ps-cell-sub ps-clip'>
																<FontAwesomeIcon icon={faEnvelope} className='me-1' />
																General contact
															</span>
														) : (
															<span className='ps-clip' title={first?.name || ''}>
																{first?.name || '—'}
																{first?.age ? <span className='ps-cell-sub'> · age {first.age}</span> : null}
																{extra > 0 ? <div className='ps-cell-sub'>+{extra} more</div> : null}
																{/* A free-trial booking is a real APPOINTMENT, not an
																    inquiry — surface when it is or the only way to see
																    it is raw Firestore. August 2026 phase; kept as
																    history after the /trial page was deleted. */}
																{l.trialBooking ? (
																	<div className='ps-cell-sub'>
																		<FontAwesomeIcon icon={faCalendarCheck} className='me-1' />
																		Trial {fmtTrialSlot(l.trialBooking)}
																	</div>
																) : null}
																{/* An UNFINISHED /register: step 1 saved, times never sent.
																    Read as "filled out the wrong form" until 2026-09-07 —
																    the marker was stored on 08-23 and never shown. */}
																{isUnfinishedStep1(l) ? (
																	<div className='ps-cell-sub inq-step1' title='Saved at step 1 of /register — no times were sent, so it is not on the Registration tab'>
																		<FontAwesomeIcon icon={faHourglassHalf} className='me-1' />
																		{step1Line(l)}
																	</div>
																) : null}
															</span>
														)}
													</td>
													<td className='col--parent' data-label='Parent'>
														<span className='ps-clip' title={l.parent?.name || ''}>{l.parent?.name || '—'}</span>
													</td>
													<td className='col--contact' data-label='Contact'>
														{/* title carries both lines — the phone is the one most often
														    clipped, and it is the reason someone opens this column. */}
														<span
															className='ps-clip'
															title={[l.parent?.email, l.parent?.phone].filter(Boolean).join(' · ')}>
															<div>{l.parent?.email || '—'}</div>
															<div className='ps-cell-sub'>{l.parent?.phone || ''}</div>
														</span>
													</td>
													<td className='col--interest' data-label='Interest'>
														<span className='ps-clip' title={interestList(first?.lessonInterest).join(', ')}>
															{interestList(first?.lessonInterest).join(', ') || '—'}
														</span>
													</td>
													<td className='col--location' data-label='Location'>
														<span className='ps-clip' title={first?.locationPreference || ''}>
															{first?.locationPreference || '—'}
														</span>
													</td>
													<td className='col--referral' data-label='Referral'>
														<span className='ps-clip' title={referralText(l)}>{referralText(l)}</span>
													</td>
													<td className='col--status' data-label='Status'>
														<span className={`ps-badge ${PS_BADGE[status] || 'ps-badge--neutral'}`}>
															{STATUS_LABEL[status] || status}
														</span>
													</td>
													<td className='col--submitted' data-label='Submitted'>{fmtDate(l.createdAt)}</td>
													{/* Full-width when stacked: the stage <select> + Convert button
													    need the whole card row, and the controls are self-labeling. */}
													<td className='ps-cell-full col--actions' onClick={(e) => e.stopPropagation()}>
														{status === 'converted' ? (
															<span className='ps-cell-sub'>
																Converted
																{Array.isArray(l.studentIds) && l.studentIds.length > 0 && (
																	<>
																		{' · '}
																		<Link to={`/portal/work/students/${l.studentIds[0]}`}>
																			view student
																		</Link>
																	</>
																)}
															</span>
														) : (
															<div className='inq-actions'>
																<select
																	className='ps-select'
																	style={{ width: 'auto' }}
																	value={status}
																	disabled={busyId === l.id}
																	onChange={(e) => changeStatus(l, e.target.value)}
																	title='Set stage'>
																	{!FUNNEL.includes(status) && (
																		<option value={status}>{STATUS_LABEL[status] || status}</option>
																	)}
																	{FUNNEL.map((s) => (
																		<option key={s} value={s}>
																			{STATUS_LABEL[s]}
																		</option>
																	))}
																</select>
																{l.kind !== 'contact' && (
																	<wa-button
																		appearance='outlined'
																		variant='brand'
																		size='small'
																		onClick={() => openConvert(l)}>
																		<FontAwesomeIcon icon={faUserPlus} /> Convert
																	</wa-button>
																)}
															</div>
														)}
													</td>
												</tr>
												{expandedId === l.id && (
													<tr className='inq-detail-row'>
														<td colSpan={9} className='ps-cell-full'>
															<div className='inq-detail'>
																{l.kind === 'contact' ? (
																	<>
																		<div>
																			<strong>From:</strong> {l.parent?.name || '—'}
																			{l.parent?.phone ? ` · ${l.parent.phone}` : ''}
																		</div>
																		<div>
																			<strong>Message:</strong> {l.message || '—'}
																		</div>
																	</>
																) : (
																	<>
																		{students.map((s, i) => (
																			<div key={i}>
																				<strong>{s.name || `Student ${i + 1}`}</strong>
																				{s.age ? ` · age ${s.age}` : ''}
																				{s.pronouns ? ` · ${s.pronouns}` : ''}
																				{s.school ? ` · ${s.school}` : ''}
																				{interestList(s.lessonInterest).length ? ` · ${interestList(s.lessonInterest).join(', ')}` : ''}
																				{s.locationPreference ? ` · ${s.locationPreference}` : ''}
																			</div>
																		))}
																		<div>
																			<strong>Parent:</strong> {l.parent?.name || '—'}
																			{l.parent?.address ? ` · ${l.parent.address}` : ''}
																		</div>
																		<div>
																			<strong>Referral:</strong> {l.referralSource || '—'}
																			{l.referredBy ? ` · thank: ${l.referredBy}` : ''}
																		</div>
																		<div>
																			<strong>Notes:</strong> {l.notes || '—'}
																		</div>
																	</>
																)}
															</div>
														</td>
													</tr>
												)}
											</React.Fragment>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>
				)}
				<wa-dialog
					ref={convertRef}
					label='Convert inquiry to student'>
					{convert && !convert.result && (
						<div className='d-flex flex-column gap-3'>
							<div>
								<strong>Guardian (payer)</strong>
								<Row className='g-2 mt-1'>
									<Col xs={6}>
										<Form.Control placeholder='First name' value={convert.guardian.firstName} onChange={(e) => setGuardian('firstName', e.target.value)} />
									</Col>
									<Col xs={6}>
										<Form.Control placeholder='Last name' value={convert.guardian.lastName} onChange={(e) => setGuardian('lastName', e.target.value)} />
									</Col>
									<Col xs={12} md={6}>
										<Form.Control type='email' placeholder='Email' value={convert.guardian.email} onChange={(e) => setGuardian('email', e.target.value)} />
									</Col>
									<Col xs={12} md={6}>
										<Form.Control placeholder='Phone' value={convert.guardian.phone} onChange={(e) => setGuardian('phone', e.target.value)} />
									</Col>
									<Col xs={12}>
										<Form.Control placeholder='Address' value={convert.guardian.address} onChange={(e) => setGuardian('address', e.target.value)} />
									</Col>
								</Row>
							</div>
							<div>
								<strong>Student{convert.students.length === 1 ? '' : 's'}</strong>
								{convert.students.map((s, i) => (
									<Row className='g-2 mt-1' key={i}>
										<Col xs={6} md={3}>
											<Form.Control placeholder='First' value={s.firstName} onChange={(e) => updateStudent(i, 'firstName', e.target.value)} />
										</Col>
										<Col xs={6} md={3}>
											<Form.Control placeholder='Last' value={s.lastName} onChange={(e) => updateStudent(i, 'lastName', e.target.value)} />
										</Col>
										<Col xs={6} md={3}>
											<Form.Control placeholder='Age' value={s.age} onChange={(e) => updateStudent(i, 'age', e.target.value)} />
										</Col>
										<Col xs={6} md={3}>
											<Form.Control placeholder='Instrument' value={s.instrument} onChange={(e) => updateStudent(i, 'instrument', e.target.value)} />
										</Col>
									</Row>
								))}
							</div>
							<p className='inq-note'>
								Creates a family (deduped by guardian email) + a student record per child. No billing or enrollment — that happens later.
							</p>
							{convert.error && <div className='text-danger'>{convert.error}</div>}
						</div>
					)}
					{convert && convert.result && (
						<div className='d-flex flex-column gap-2'>
							<div>
								Created {convert.result.studentIds.length} student record
								{convert.result.studentIds.length === 1 ? '' : 's'} under one family.
							</div>
							{convert.result.studentIds.map((id, i) => (
								<Link key={id} to={`/portal/work/students/${id}`}>
									View {`${convert.students[i]?.firstName || ''} ${convert.students[i]?.lastName || ''}`.trim() || `student ${i + 1}`}
								</Link>
							))}
						</div>
					)}
					<div slot='footer' className='d-flex gap-2 justify-content-end'>
						{convert && !convert.result ? (
							<>
								<wa-button appearance='plain' onClick={() => setConvert(null)}>
									Cancel
								</wa-button>
								<WaButton
									variant='brand'
									loading={convert.saving}
									disabled={convert.saving}
									onClick={submitConvert}>
									Convert to student
								</WaButton>
							</>
						) : (
							<wa-button variant='brand' onClick={() => setConvert(null)}>
								Done
							</wa-button>
						)}
					</div>
				</wa-dialog>
			</div>
		</>
	);
};

export default Inquiries;
